/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { Alert, Snackbar } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import loginLogo from '../assets/login.png';
import ProfileStyles from '../css/UserProfileDropdown.module.css';
import { useAuthContext } from '../contexts/AuthContext';
import { useLoanContext } from '../contexts/LoanContext';
// import { useRootContext } from '../contexts/RootContext';

import PrivacySettingsModal from '../components/modals/PrivacySettingsModal';
import LoadingModal from './modals/LoadingModal';
import { useBrwrContext } from '../contexts/BorrowerContext';
import { isObjEmptyOrNull, isStrEmptyOrNull } from '../common/UtilityFunctions';
import useAbortController from '../common/hooks/useAbortController';
import axiosCall from '../services/axios';
import { useViewingContext } from '../contexts/ViewingContext';
import CreateCommentModal from './modals/CreateCommentModal';
import { useLoginContext } from './../contexts/LoginContext';

const UserProfileDropdown = () => {
  const { logout: authLogout, servicerUser, firstName, lastName, token, userId, superUser } = useAuthContext();
  const { logout: borrowerLogout } = useLoginContext();
  const { loanNumber } = useLoanContext();
  const { isServicerView, toggleBorrowerView } = useViewingContext();
  // const { closeNavIfMobile } = useRootContext();
  const { currentBorrower } = useBrwrContext();
  const { reset } = useAbortController();

  const [openSunsoftStatus, setOpenSunsoftStatus] = useState('done');
  const [loadingMessage, setLoadingMessage] = useState('');
  const [statusAlert, setStatusAlert] = useState({
    status: '',
    message: '',
  });

  const status = useMemo(() => {
    const statuses = [openSunsoftStatus];

    if (statuses.includes('error')) {
      return 'error';
    } else if (statuses.includes('loading')) {
      return 'loading';
    }

    return 'done';
  }, [openSunsoftStatus]);

  const [privacyModalNumber, setPrivacyModalNumber] = useState('');
  const onModalClose = useCallback(() => {
    setPrivacyModalNumber('');
  }, []);

  const getProfileDisplayName = useCallback(() => {
    let displayName = 'Account';
    if (!servicerUser && !isObjEmptyOrNull(currentBorrower)) {
      if (!isStrEmptyOrNull(currentBorrower.firstName) || !isStrEmptyOrNull(currentBorrower.lastName)) {
        displayName = currentBorrower.firstName + ' ' + currentBorrower.lastName;
      }
    } else if (servicerUser && (!isStrEmptyOrNull(firstName) || !isStrEmptyOrNull(lastName))) {
      displayName = firstName + ' ' + lastName;
    }
    return displayName;
  }, [currentBorrower, servicerUser, firstName, lastName]);

  const sunsoftLoginServicer = useCallback(
    async (useLoan: boolean, newTab: WindowProxy) => {
      try {
        if (!token) return;
        setOpenSunsoftStatus('loading');
        setLoadingMessage('Opening Sunsoft...');

        // send request to /live/system/access endpoint to retrieve a link
        let request;
        if (useLoan) {
          if (!loanNumber) throw Error(`No loan number found in loanContext.`);
          request = {
            url: `${process.env.REACT_APP_SYSTEM_USER_ACCESS}`,
            method: 'POST',
            data: {
              portal: 'sunsoft-servicer',
              loan: loanNumber,
            },
          };
        } else {
          request = {
            url: `${process.env.REACT_APP_SYSTEM_USER_ACCESS}`,
            method: 'POST',
            data: {
              portal: 'sunsoft-servicer',
            },
          };
        }
        const config = reset();
        const headers = {
          'Session-Token': token,
          'Content-Type': 'application/json',
        };

        const response = await axiosCall(request, config, headers);

        // check if response was successful
        if (!response) throw new Error(`Invalid response from System Access endpoint.`);

        if (!response?.url) throw new Error(response?.message);

        // open link
        newTab.location = response?.url;
        setOpenSunsoftStatus('done');
      } catch (error) {
        console.error(`Error opening Sunsoft for user ${userId}: ${error.message}`);
        setStatusAlert({ status: 'error', message: 'Error opening Sunsoft.' });
        setOpenSunsoftStatus('error');
      }
    },
    [token, reset, userId, loanNumber],
  );

  const openSunsoft = useCallback(() => {
    const newTab = window.open();
    sunsoftLoginServicer(false, newTab);
  }, [sunsoftLoginServicer]);

  const openSunsoftWithLoan = useCallback(() => {
    const newTab = window.open();
    sunsoftLoginServicer(true, newTab);
  }, [sunsoftLoginServicer]);

  const handleLogout = useCallback(async (): Promise<void> => {
    // Avoid creating borrower login audit record when servicer toggles borrower view
    if (servicerUser) {
      await authLogout();
      return;
    }

    await borrowerLogout();
  }, [authLogout, borrowerLogout, servicerUser]);

  return (
    <div className={ProfileStyles.profileHeader}>
      {status === 'loading' && <LoadingModal open={status === 'loading'} loadingMessage={loadingMessage} />}
      <Snackbar
        open={!!statusAlert.message}
        onClose={() =>
          setStatusAlert({
            status: '',
            message: '',
          })
        }
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        ClickAwayListenerProps={{ onClickAway: () => null }}
      >
        <Alert
          onClose={() =>
            setStatusAlert({
              status: '',
              message: '',
            })
          }
          severity={statusAlert.status as any}
          variant='filled'
          // className={Styles.alert}
        >
          {statusAlert.message}
        </Alert>
      </Snackbar>
      {/* Main Box */}
      <details>
        <summary>
          {/* User Logo */}
          <img src={loginLogo} alt='User Logo' />

          {/* Login Name */}
          <span className={ProfileStyles.loginName}>{getProfileDisplayName()}</span>
          {/* Ellipsis Icon */}
          <span>⋮</span>
        </summary>
      </details>
      {/* Dropdown List */}
      <div>
        <div>
          <ul className={!isServicerView ? ProfileStyles.borrowerMenu : undefined}>
            {loanNumber && !isServicerView && (
              <li>
                <NavLink to='/my-profile'>My Profile</NavLink>
              </li>
            )}
            {loanNumber && !isServicerView && (
              <li>
                <button onClick={() => setPrivacyModalNumber(currentBorrower?.borrowerNumber || '01')}>
                  Privacy Settings
                </button>
              </li>
            )}
            {/* <li><NavLink onClick={closeNavIfMobile} to='/account-security'>Account Security</NavLink></li> */}
            <li>
              <button onClick={handleLogout}>Logout</button>
            </li>
            {isServicerView && superUser && (
              <li>
                <button onClick={openSunsoft}>Open Sunsoft</button>
              </li>
            )}
            {isServicerView && loanNumber && superUser && (
              <li>
                <button onClick={openSunsoftWithLoan}>Open Loan in Sunsoft</button>
              </li>
            )}
            {servicerUser && loanNumber && (
              <li>
                <button onClick={toggleBorrowerView}>Toggle {isServicerView ? 'Borrower' : 'Servicer'} View</button>
              </li>
            )}
          </ul>
        </div>
      </div>
      <PrivacySettingsModal borrowerNumber={privacyModalNumber} open={privacyModalNumber} onClose={onModalClose} />
    </div>
  );
};

export default UserProfileDropdown;
